import React, { useEffect, useState } from "react";
import { MapPin, ClipboardList, PackageCheck, Key } from "lucide-react";
import {decryptData} from '../utils/storageCrypto'
/* -------------------- HELPERS -------------------- */

const safeVendorName = (vendorObj) => {
  if (!vendorObj) return "Unknown Location";
  if (typeof vendorObj === "string") return vendorObj;
  return vendorObj.company_name || vendorObj.name || "Pickup Location";
};

const buildNavigateUrl = (loc) => {
  if (!loc) return null;
  const lat = loc.lat ?? loc.latitude ?? loc.latLng?.lat;
  const lng = loc.lng ?? loc.longitude ?? loc.latLng?.lng;
  if (lat == null || lng == null) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

/* -------------------- BACKEND → UI STATUS MAP -------------------- */

const mapBackendStatusToUI = (status, otpGenerated) => {
  if (!status) return "pending";

  const s = status.toLowerCase();

  if (s.includes("picked")) return "done";
  if (otpGenerated) return "otp";
  if (s.includes("ready") || s.includes("pending")) return "pending";

  return "pending";
};

/* -------------------- CARD (UI UPDATED FOR RESEND) -------------------- */

const PickupCard = ({
  sub,
  index,
  status,
  isActive,
  otpValue,
  setOtpValue,
  onReached,
  onVerifyOTP,
  otpGenTime,
  now,
  onResendOTP,
  loading,
}) => {
  const vendor = sub.vendor_id;
  const vendorLoc = vendor?.location || sub.vendor_location || null;
  const navigateUrl = buildNavigateUrl(vendorLoc);

  return (
    <div className="bg-white border-2 border-gray-200 p-5 mb-4 rounded-2xl shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h4 className="font-bold text-gray-900 mb-1">
            Pickup #{index + 1}
          </h4>
          <div className="text-sm text-gray-700">
            <span className="font-semibold">
              {safeVendorName(vendor)}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Order: {sub?.purchaseorder_id?.quick_buy_sub_order_number || "N/A"}
          </div>
        </div>

        <span
          className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap ${
            status === "pending"
              ? "bg-gray-200 text-gray-700"
              : status === "reached"
              ? "bg-yellow-100 text-yellow-800 border-2 border-yellow-300"
              : status === "otp"
              ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
              : "bg-green-100 text-green-800 border-2 border-green-300"
          }`}
        >
          {status === "pending"
            ? "GO"
            : status === "reached"
            ? "LOADING"
            : status === "otp"
            ? "OTP"
            : "DONE"}
        </span>
      </div>

      {status !== "done" && navigateUrl && (
        <a
          href={navigateUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-3 px-4 py-4 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors mb-3"
        >
          <MapPin className="w-6 h-6" />
          <span className="font-bold">Navigate</span>
        </a>
      )}

      {isActive && status !== "done" && (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-3 text-gray-900 font-bold">
            <ClipboardList className="w-5 h-5" />
            <span>Items to Pickup</span>
          </div>

          <ul className="text-sm border-2 border-gray-200 rounded-xl p-4 bg-gray-50 space-y-2">
            {(sub.items || []).map((it, idx) => (
              <li
                key={idx}
                className="flex justify-between py-2 border-b border-gray-200 last:border-0"
              >
                <span className="font-medium text-gray-800">
                  {it.item_name}
                </span>
                <span className="font-bold text-gray-900">
                  {it.quantity} {it.UOM}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isActive && status === "pending" && (
        <button
          onClick={() => onReached(sub.purchaseorder_id._id)}
          disabled={loading}
          className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
        >
          {loading ? "Loading..." : " Reached"}
        </button>
      )}

      {isActive && status === "otp" && (
        <div>
          <div className="flex items-center gap-3 text-gray-900 font-bold mb-4">
            <Key className="w-5 h-5" />
            <span>Enter OTP</span>
          </div>

          <input
            maxLength={4}
            value={otpValue}
            onChange={(e) => {
              const val = e.target.value;
              if (/^[0-9]{0,4}$/.test(val)) setOtpValue(val);
            }}
            className="border-2 border-gray-300 rounded-xl w-full p-4 text-center text-2xl font-bold mb-4 tracking-widest focus:outline-none focus:border-blue-500"
            placeholder="----"
            inputMode="numeric"
            autoFocus
          />

        {/*   {otpGenTime &&  (
            <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
              
              <div>
                <button
                  onClick={() =>
                    onResendOTP(sub.purchaseorder_id._id)
                  }
                  className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-800"
                >
                  Resend
                </button>
              </div>
            </div>
          )} */}

          <button
            disabled={otpValue.length !== 4 || loading}
            onClick={() =>
              onVerifyOTP(sub.purchaseorder_id._id)
            }
            className={`w-full py-4 rounded-xl font-bold transition-colors ${
              otpValue.length === 4 && !loading
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </div>
      )}

      {status === "done" && (
        <div className="flex items-center gap-3 text-green-700 font-bold bg-green-50 p-4 rounded-xl border-2 border-green-200">
          <PackageCheck className="w-6 h-6" />
          <span>Picked Up</span>
        </div>
      )}
    </div>
  );
};

/* -------------------- MAIN PAGE -------------------- */

const PickupsPage = ({ deliveryGroup, onComplete }) => {
  const subOrders = deliveryGroup?.sub_orders || [];

  const [statuses, setStatuses] = useState(() => {
    const initial = {};
    subOrders.forEach((s) => {
      const po = s.purchaseorder_id;
      const otpGenerated = !!po?.order_pickup_otp;
      initial[po._id] = mapBackendStatusToUI(s.status, otpGenerated);
    });
    return initial;
  });

  const [otpGenTimes, setOtpGenTimes] = useState(() => {
    const t = {};
    subOrders.forEach((s) => {
      const ts = s.purchaseorder_id?.updated_at;
      if (ts) t[s.purchaseorder_id._id] = new Date(ts).getTime();
    });
    return t;
  });

  const [currentPo, setCurrentPo] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const otpPo = subOrders.find(
      (s) => statuses[s.purchaseorder_id._id] === "otp"
    );
    if (otpPo) {
      setCurrentPo(otpPo.purchaseorder_id._id);
      return;
    }

    const pendingPo = subOrders.find(
      (s) => statuses[s.purchaseorder_id._id] === "pending"
    );
    if (pendingPo) {
      setCurrentPo(pendingPo.purchaseorder_id._id);
    }
  }, [statuses, subOrders]);

  const updateStatus = (poId, status) =>
    setStatuses((p) => ({ ...p, [poId]: status }));

  const getNextPo = (currentPoId) => {
    const currentIndex = subOrders.findIndex(
      (s) => s.purchaseorder_id._id === currentPoId
    );
    for (let i = currentIndex + 1; i < subOrders.length; i++) {
      const poId = subOrders[i].purchaseorder_id._id;
      if (statuses[poId] === "pending") return poId;
    }
    return null;
  };

  const handleReached = async (poId) => {
    setLoading(true);
    updateStatus(poId, "reached");
    await handleGenerateOTP(poId);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);
  };

  const handleGenerateOTP = async (poId) => {
    try {
      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);
      const res = await fetch(
        `${URL}customer/purchaseorder/generate-order-pickup-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" ,
            "x-token":token
          },
          body: JSON.stringify({
            delivery_group_id: deliveryGroup._id,
            po_id: poId,
          }),
        }
      );

      const data = await res.json();
      //console.log("Generated OTP Response:", data);
      if (!data.success) return alert(data.message);
      console.log("Generated OTP:", data);
      updateStatus(poId, "otp");
      setOtpGenTimes((prev) => ({ ...prev, [poId]: Date.now() }));
    } catch (error) {
      console.error("Failed to generate OTP", error);
      //alert("Failed to generate OTP");
    }
  };

  const handleResendOTP = async (poId) => {
    setLoading(true);
    await handleGenerateOTP(poId);
    setLoading(false);
  };

  const handleVerifyOTP = async (poId) => {
    try {
      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/purchaseorder/verify-order-pickup-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            po_id: poId,
            otp: Number(otpValue),
          }),
        }
      );

      const data = await res.json();
      if (!data.success) return alert(data.message);

      updateStatus(poId, "done");
      setOtpGenTimes((prev) => {
        const copy = { ...prev };
        delete copy[poId];
        return copy;
      });

      const next = getNextPo(poId);
      if (next) {
        setCurrentPo(next);
        setOtpValue("");
      } else {
        onComplete && onComplete();
      }
    } catch {
      alert("OTP verification failed");
    }
  };

  const allDone = Object.values(statuses).every((s) => s === "done");

  return (
    <div className="p-5">
      <h3 className="font-bold text-gray-900 mb-5">Pickups</h3>

      {subOrders.map((sub, i) => (
        <PickupCard
          key={sub.purchaseorder_id._id}
          sub={sub}
          index={i}
          status={statuses[sub.purchaseorder_id._id]}
          isActive={currentPo === sub.purchaseorder_id._id}
          otpValue={otpValue}
          setOtpValue={setOtpValue}
          onReached={handleReached}
          onVerifyOTP={handleVerifyOTP}
          otpGenTime={otpGenTimes[sub.purchaseorder_id._id]}
          now={now}
          onResendOTP={handleResendOTP}
          loading={loading}
        />
      ))}

      {allDone && (
        <button
          onClick={onComplete}
          className="w-full mt-5 py-5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg"
        >
          Start Delivery
        </button>
      )}
    </div>
  );
};

export default PickupsPage;